import type { Translation } from "./en";

/** German strings (informal "du"), using the DE terms from CONTEXT.md. */
export const de: Translation = {
  app: {
    name: "Bimmertools Inventory",
    tagline: "Teile, Fahrzeuge und Arbeitsaufträge an einem Ort.",
  },
  nav: {
    overview: "Übersicht",
    inventory: "Lager",
    vehicles: "Fahrzeuge",
    workOrders: "Arbeitsaufträge",
    templates: "Vorlagen",
    orders: "Bestellungen",
    toOrder: "Zu bestellen",
    settings: "Einstellungen",
    search: "Suche",
    searchHint: "Suchen…",
    workshop: "Werkstatt",
    purchasing: "Einkauf",
  },
  common: {
    save: "Speichern",
    saveChanges: "Änderungen speichern",
    cancel: "Abbrechen",
    delete: "Löschen",
    edit: "Bearbeiten",
    add: "Hinzufügen",
    create: "Anlegen",
    close: "Schließen",
    undo: "Rückgängig",
    remove: "Entfernen",
    archive: "Archivieren",
    unarchive: "Wiederherstellen",
    archived: "Archiviert",
    search: "Suchen",
    retry: "Erneut versuchen",
    optional: "optional",
    none: "Keine",
    all: "Alle",
    notes: "Notizen",
    description: "Beschreibung",
    name: "Name",
    title: "Titel",
    type: "Art",
    status: "Status",
    copy: "Kopieren",
    more: "Mehr",
    details: "Details",
    documents: "Dokumente",
    links: "Links",
    total: "Gesamt",
    price: "Preis",
    quantity: "Menge",
    created: "Angelegt",
    deleteConfirmTitle: "{{name}} löschen?",
    deleteConfirmBody: "Das lässt sich nicht rückgängig machen.",
    select: "Auswählen…",
    view: "Ansehen",
  },
  validation: {
    required: "Pflichtfeld",
    tooLong: "Zu lang",
    tooLarge: "Zu groß",
    number: "Bitte eine Zahl eingeben",
    positive: "Muss größer als 0 sein",
    nonNegative: "Darf nicht negativ sein",
    nonZero: "Darf nicht 0 sein",
    date: "Bitte ein gültiges Datum eingeben",
    url: "Bitte eine Webadresse mit http:// oder https:// eingeben",
    year: "Bitte ein Jahr zwischen 1885 und 2100 eingeben",
    taxRate: "Bitte einen Satz zwischen 0 und 100 % eingeben",
    taxRates: "Pro Tag ist nur ein MwSt.-Satz möglich, und es muss einen Grundsatz geben",
    untilBeforeFrom: "Darf nicht vor dem Startdatum liegen",
    email: "Bitte eine E-Mail-Adresse eingeben",
    oneTarget: "Bitte entweder eine Bestellung oder einen Händler wählen",
    passwordTooShort: "Mindestens 8 Zeichen",
    passwordsDiffer: "Die Passwörter stimmen nicht überein",
    emailTwice: "Jede E-Mail-Adresse nur einmal",
    inviteDays: "Bitte 1 bis 90 Tage eingeben",
  },
  errors: {
    NOT_FOUND: "Diesen Eintrag gibt es nicht mehr.",
    UNAUTHORIZED: "Die Sitzung ist abgelaufen. Bitte starte die App neu.",
    FORBIDDEN: "Das ist hier nicht erlaubt.",
    GARAGE_NOT_FOUND: "Diese Garage gibt es nicht.",
    GARAGE_REQUIRED: "Öffne zuerst eine Garage.",
    GARAGE_NAME_INVALID: "Ein Garagenname braucht 3 bis 50 Buchstaben, Ziffern oder Bindestriche.",
    GARAGE_NAME_RESERVED:
      "Garagennamen dürfen nicht mit einem reservierten Wort wie demo oder bmw beginnen.",
    GARAGE_NAME_TAKEN: "Es gibt schon eine Garage mit diesem Namen.",
    PART_IN_USE:
      "Dieses Teil wird in Arbeitsaufträgen, Vorlagen oder Bestellungen verwendet. Archiviere es stattdessen.",
    PART_LINK_INVALID: "Nur ein Originalteil lässt sich mit einem OE- oder Nachbauteil verknüpfen.",
    PART_LINK_TAKEN: "Dieses Teil ist schon mit einem anderen Originalteil verknüpft.",
    LOCATION_CYCLE: "Ein Lagerort kann nicht in sich selbst liegen.",
    CHASSIS_CODE_EXISTS: "Diese Baureihe gibt es schon.",
    MOVEMENT_NOT_UNDOABLE:
      "Diese Änderung lässt sich nur über ihre Bestellung oder ihren Arbeitsauftrag rückgängig machen.",
    WORK_ORDER_COMPLETED:
      "Dieser Arbeitsauftrag ist abgeschlossen. Öffne ihn erneut, um etwas zu ändern.",
    WORK_ORDER_NOT_COMPLETED: "Dieser Arbeitsauftrag ist nicht abgeschlossen.",
    ORDER_NOT_EDITABLE: "Diese Bestellung kann nicht mehr geändert werden.",
    ORDER_INVALID_STATUS: "Das ist im aktuellen Status der Bestellung nicht möglich.",
    ORDER_HAS_RECEIPTS:
      "Teile dieser Bestellung sind schon eingegangen. Mach zuerst die Wareneingänge rückgängig.",
    QTY_BELOW_RECEIVED: "Du kannst nicht weniger bestellen, als schon eingegangen ist.",
    FILE_TOO_LARGE: "Die Datei ist zu groß (max. 50 MB).",
    FILE_TYPE_NOT_ALLOWED: "Dieser Dateityp ist hier nicht erlaubt.",
    IMPORT_INVALID: "Diese Datei ist keine gültige Sicherung.",
    PACKAGE_INVALID: "Diese Datei ist kein Demo-Paket.",
    PACKAGE_TOO_NEW:
      "Dieses Demo-Paket stammt aus einer neueren Version von Bimmertools Inventory. Aktualisiere die App, um es zu importieren.",
    PACKAGE_TOO_OLD: "Dieses Demo-Paket ist zu alt für diese Version der App.",
    SIGN_IN_FAILED: "E-Mail-Adresse oder Passwort ist falsch.",
    WRONG_PASSWORD: "Das Passwort ist falsch.",
    ACCOUNT_DISABLED: "Dein Konto wurde gesperrt.",
    ACCOUNT_DELETED: "Dein Konto wurde gelöscht.",
    MEMBERSHIP_DEACTIVATED: "Dein Zugang zu dieser Garage wurde deaktiviert.",
    INVITATION_INVALID:
      "Diese Einladung ist abgelaufen, wurde schon angenommen oder zurückgezogen.",
    RESET_LINK_INVALID:
      "Dieser Link ist abgelaufen oder wurde schon benutzt. Fordere einen neuen an.",
    LAST_GARAGE_ADMIN:
      "Eine Garage braucht einen aktiven Garagen-Admin. Mach zuerst jemand anderen zum Garagen-Admin.",
    LAST_PLATFORM_ADMIN:
      "Es muss einen Administrator geben. Mach zuerst jemand anderen zum Administrator.",
    RATE_LIMITED: "Zu viele Versuche. Warte ein paar Minuten und versuch es dann noch einmal.",
    EMAIL_TAKEN: "Mit dieser E-Mail-Adresse gibt es schon ein Konto. Melde dich stattdessen an.",
    ALREADY_MEMBER: "Diese Person ist schon Mitglied dieser Garage.",
    PASSWORD_REQUIRED: "Bitte ein Passwort mit mindestens 8 Zeichen eingeben.",
    MAIL_FAILED: "Die E-Mail konnte nicht gesendet werden. Versuch es später noch einmal.",
    NOT_YOURSELF: "Das geht nicht mit deinem eigenen Konto.",
    UPLOADS_DISABLED: "In der Demo ist das Hochladen abgeschaltet.",
    DEMO_LINK_INVALID:
      "Dieser Link ist abgelaufen oder wurde schon benutzt. Fordere einen neuen an.",
    DEMO_BUSY: "Die Demo ist gerade ausgelastet. Bitte versuche es etwas später noch einmal.",
    generic: "Etwas ist schiefgelaufen. Bitte versuch es noch einmal.",
    network: "Der Server der App ist nicht erreichbar.",
    pageTitle: "Diese Seite konnte nicht geladen werden",
  },
  units: {
    pcs_one: "{{value}} Stk.",
    pcs_other: "{{value}} Stk.",
    pair_one: "{{value}} Paar",
    pair_other: "{{value}} Paar",
    set_one: "{{value}} Satz",
    set_other: "{{value}} Sätze",
    l: "{{value}} l",
    ml: "{{value}} ml",
    m: "{{value}} m",
    cm: "{{value}} cm",
    kg: "{{value}} kg",
    g: "{{value}} g",
  },
  unitNames: {
    pcs: "Stück",
    pair: "Paar",
    set: "Satz",
    l: "Liter",
    ml: "Milliliter",
    m: "Meter",
    cm: "Zentimeter",
    kg: "Kilogramm",
    g: "Gramm",
  },
  unitShort: {
    pcs: "Stk.",
    pair: "Paar",
    set: "Satz",
    l: "l",
    ml: "ml",
    m: "m",
    cm: "cm",
    kg: "kg",
    g: "g",
  },
  availability: {
    in_stock: "Auf Lager",
    on_order: "Bestellt",
    partial: "Teilweise",
    missing: "Fehlt",
    tool: "Werkzeug",
  },
  availabilityDetail: {
    fromStock: "{{qty}} aus dem Lager",
    fromIncoming: "{{qty}} bestellt",
    missing: "{{qty}} fehlen",
  },
  readiness: {
    ready: "Bereit",
    waiting: "Wartet auf Lieferung",
    missing: "Teile fehlen",
  },
  readinessLong: {
    ready: "Alle Teile sind da",
    readyBody_one: "Das {{count}} benötigte Teil ist auf Lager.",
    readyBody_other: "Alle {{count}} benötigten Teile sind auf Lager.",
    waiting: "Wartet auf Lieferung",
    waitingBody_one: "{{count}} Teil ist bestellt. Alles andere ist da.",
    waitingBody_other: "{{count}} Teile sind bestellt. Alles andere ist da.",
    missing: "Teile fehlen",
    missingBody_one: "{{count}} Teil ist weder auf Lager noch bestellt.",
    missingBody_other: "{{count}} Teile sind weder auf Lager noch bestellt.",
    draftsBody:
      "Alles Fehlende steht auf Bestellentwürfen. Schick sie ab, damit die Teile unterwegs sind.",
  },
  stockStatus: {
    toOrder: "Zu bestellen: {{qty}}",
    onOrder: "Bestellt",
    low: "Knapp",
    inStock: "Auf Lager",
    outOfStock: "Nicht vorrätig",
  },
  workOrderStatus: {
    idea: "Idee",
    planned: "Geplant",
    in_progress: "In Arbeit",
    completed: "Abgeschlossen",
    cancelled: "Abgebrochen",
  },
  workOrderType: {
    maintenance: "Wartung",
    repair: "Reparatur",
    tuning: "Tuning",
    restoration: "Restaurierung",
    inspection: "Inspektion",
    other: "Sonstiges",
  },
  orderStatus: {
    draft: "Entwurf",
    ordered: "Bestellt",
    partially_received: "Teilweise eingegangen",
    received: "Eingegangen",
    cancelled: "Storniert",
  },
  orderStatusGroup: {
    draft: "In Vorbereitung",
    ordered: "Wartet auf Lieferung",
    partially_received: "Teilweise eingegangen",
    received: "Eingegangen",
    cancelled: "Storniert",
  },
  partOrigin: {
    genuine: "Original",
    oe: "OE",
    aftermarket: "Nachbau",
  },
  partNumberKind: {
    primary: "Haupt",
    alternative: "Alternativ",
    superseded: "Ersetzt",
    aftermarket: "Zubehör",
  },
  movementReason: {
    initial: "Anfangsbestand",
    adjustment: "Korrigiert",
    order_receipt: "Wareneingang",
    work_order_consumption: "Verbaut",
  },
  figures: {
    onHand: "Auf Lager",
    onOrder: "Bestellt",
    required: "Benötigt",
    toOrder: "Zu bestellen",
    free: "Frei",
    inDraft: "In Entwürfen",
    minStock: "Mindestbestand",
    missing: "Fehlt",
  },
  figureHelp: {
    onHand: "Liegt gerade tatsächlich in deiner Garage.",
    onOrder: "Bestellt und noch nicht eingegangen.",
    required: "Wird von geplanten und laufenden Arbeitsaufträgen benötigt.",
    toOrder: "Was noch bestellt werden muss, um Arbeitsaufträge und Mindestbestand abzudecken.",
    free: "Auf Lager und von keinem Arbeitsauftrag benötigt.",
    inDraft: "Steht schon auf Bestellentwürfen, die noch nicht abgeschickt sind.",
  },
  overview: {
    greetingMorning: "Guten Morgen",
    greetingAfternoon: "Guten Tag",
    greetingEvening: "Guten Abend",
    subtitle: "Das ist gerade in deiner Garage los.",
    toOrderTile: "Zu bestellen",
    toOrderTileHint_one: "{{count}} Teil zu bestellen",
    toOrderTileHint_other: "{{count}} Teile zu bestellen",
    readyTile: "Startklar",
    readyTileHint: "von {{total}} geplanten",
    arrivingTile: "Unterwegs",
    arrivingTileHint_one: "{{count}} Bestellung",
    arrivingTileHint_other: "{{count}} Bestellungen",
    lowTile: "Knapper Bestand",
    lowTileHint: "unter Mindestbestand",
    upNext: "Als Nächstes",
    upNextEmpty: "Nichts geplant. Plane einen Arbeitsauftrag, um zu sehen, was du brauchst.",
    arriving: "Kommt bald",
    arrivingEmpty: "Nichts unterwegs.",
    orderedOn: "Bestellt am {{date}}",
    lowStock: "Unter Mindestbestand",
    recent: "Letzte Lagerbewegungen",
    recentEmpty: "Lagerbewegungen erscheinen hier.",
    welcomeTitle: "Willkommen in deiner Garage",
    welcomeBody:
      "Behalte jedes Teil im Regal im Blick, was jede Arbeit braucht und was noch bestellt werden muss.",
    stepVehicle: "Fahrzeug hinzufügen",
    stepVehicleBody: "Das Auto oder Projekt, an dem du schraubst.",
    stepParts: "Teile hinzufügen",
    stepPartsBody: "Was im Regal liegt – und was du sicher brauchen wirst.",
    stepWork: "Arbeitsauftrag planen",
    stepWorkBody: "Sieh sofort, welche Teile fehlen.",
    demo: "Mit Beispieldaten ausprobieren",
    demoBody:
      "Zwei Beispielautos mit Teilen, Arbeitsaufträgen und Bestellungen. Du kannst alles später in den Einstellungen löschen.",
    demoPackageBody:
      "Schau dich mit Beispielautos, Teilen, Arbeitsaufträgen und Bestellungen um: Lade das Demo-Paket herunter und importiere es hier.",
    viewAll: "Alle anzeigen",
  },
  parts: {
    title: "Lager",
    count_one: "{{count}} Teil",
    count_other: "{{count}} Teile",
    new: "Neues Teil",
    edit: "Teil bearbeiten",
    searchPlaceholder: "Name oder Teilenummer suchen…",
    filterAll: "Alle",
    filterToOrder: "Zu bestellen",
    filterMissing: "Fehlt",
    filterLow: "Knapp",
    filterOnOrder: "Bestellt",
    filterInStock: "Auf Lager",
    filterOutOfStock: "Nicht vorrätig",
    filterArchived: "Archiviert",
    anyLocation: "Alle Lagerorte",
    anyTag: "Alle Schlagwörter",
    emptyTitle: "Noch keine Teile",
    emptyBody:
      "Erfasse die Teile in deinem Regal – und die, von denen du weißt, dass du sie brauchen wirst, auch bevor du sie kaufst.",
    noMatchTitle: "Keine passenden Teile",
    noMatchBody: "Versuch eine andere Suche oder setz die Filter zurück.",
    clearFilters: "Filter zurücksetzen",
    columnPart: "Teil",
    columnLocation: "Lagerort",
    columnStatus: "Status",
    name: "Name",
    namePlaceholder: "z. B. Ölfilter",
    manufacturer: "Hersteller",
    manufacturerPlaceholder: "z. B. BMW, MANN, Bosch",
    origin: "Herkunft",
    originNone: "Keine Angabe",
    originHint:
      "Original: vom Fahrzeughersteller. OE: vom Zulieferer des Herstellers, in eigener Verpackung. Nachbau: von einem anderen Hersteller.",
    allOrigins: "Jede Herkunft",
    genuinePart: "Originalteil",
    genuinePartBody: "Das Originalteil, zu dem dieses Teil eine Alternative ist.",
    alternatives: "Alternativen",
    alternativesBody:
      "OE- und Nachbauteile, die dieselbe Aufgabe erfüllen. Jedes hat eigenen Bestand, eigene Bezugsquellen und Preise.",
    noGenuinePart: "Noch kein Originalteil verknüpft.",
    noAlternatives: "Noch keine Alternativen verknüpft.",
    linkGenuine: "Originalteil verknüpfen",
    linkAlternative: "Alternative verknüpfen",
    linkSearch: "Teile oder Nummern suchen…",
    linkNone: "Kein Teil mit passender Herkunft gefunden.",
    unlink: "Verknüpfung lösen",
    linked: "Teile verknüpft",
    unlinked: "Verknüpfung gelöst",
    numbers: "Teilenummern",
    numbersHint: "Leerzeichen und Bindestriche spielen bei der Suche keine Rolle.",
    addNumber: "Nummer hinzufügen",
    numberPlaceholder: "11 42 7 512 300",
    unit: "Einheit",
    initialQty: "Menge auf Lager",
    initialQtyHint: "Gib 0 ein, wenn du es noch nicht hast.",
    minStock: "Mindestbestand",
    minStockHint: "Mindestens so viel auf Lager halten. Leer lassen für keinen.",
    location: "Lagerort",
    locationDetail: "Genauer Platz",
    locationDetailPlaceholder: "z. B. Fach 3",
    tags: "Schlagwörter",
    addTag: "Schlagwort hinzufügen",
    newTag: "Schlagwort „{{name}}“ anlegen",
    duplicateNumber: "{{number}} wird auch verwendet von",
    created: "Teil angelegt",
    saved: "Teil gespeichert",
    deleted: "Teil gelöscht",
    archived: "Teil archiviert",
    restored: "Teil wiederhergestellt",
    stock: "Bestand",
    setExact: "Genaue Menge setzen",
    setExactTitle: "Menge auf Lager setzen",
    setExactBody: "Nach dem Zählen verwenden. Die Differenz wird als Korrektur erfasst.",
    adjusted: "{{name}}: {{delta}}",
    undone: "Änderung rückgängig gemacht",
    increase: "Erhöhen",
    decrease: "Verringern",
    whereUsed: "Verwendung",
    whereUsedEmpty: "Wird noch in keinem Arbeitsauftrag und keiner Vorlage verwendet.",
    inTemplates: "In Vorlagen",
    orders: "Bestellungen",
    ordersEmpty: "Dieses Teil wurde noch nicht bestellt.",
    history: "Verlauf",
    historyEmpty: "Noch keine Lagerbewegungen.",
    sources: "Bezugsquellen",
    sourcesEmpty: "Füge Händler und Links hinzu, um mit einem Klick nachzubestellen.",
    addSource: "Bezugsquelle hinzufügen",
    editSource: "Bezugsquelle bearbeiten",
    supplier: "Händler",
    newSupplier: "Händler „{{name}}“ anlegen",
    url: "Link",
    supplierPartNumber: "Artikelnummer beim Händler",
    preferred: "Bevorzugt",
    preferredHint: "Wird verwendet, um Teile auf Bestellentwürfe zu verteilen.",
    openShop: "Im Shop öffnen",
    noDescription: "Keine Beschreibung.",
    addToOrder: "Zur Bestellung",
    deleteBlocked: "Dieses Teil wird noch verwendet. Archiviere es, um es im Lager auszublenden.",
    archivedBanner: "Dieses Teil ist archiviert und im Lager ausgeblendet.",
  },
  vehicles: {
    title: "Fahrzeuge",
    count_one: "{{count}} Fahrzeug",
    count_other: "{{count}} Fahrzeuge",
    new: "Neues Fahrzeug",
    edit: "Fahrzeug bearbeiten",
    emptyTitle: "Noch keine Fahrzeuge",
    emptyBody:
      "Füge die Autos, Motorräder oder Motorprojekte hinzu, an denen du schraubst. Arbeitsaufträge und Servicehistorie hängen daran.",
    name: "Name",
    namePlaceholder: "z. B. E46 330Ci Coupé",
    make: "Marke",
    makePlaceholder: "z. B. BMW",
    model: "Modell",
    modelPlaceholder: "z. B. 325i",
    variant: "Variante",
    variantPlaceholder: "z. B. Coupé",
    modelYear: "Modelljahr",
    productionDate: "Produktionsdatum",
    vin: "FIN",
    chassisCode: "Baureihe",
    engineCode: "Motorkennung",
    transmission: "Getriebe",
    paintCode: "Farbcode",
    licensePlate: "Kennzeichen",
    mileage: "Kilometerstand",
    mileageUnit: "Einheit",
    identity: "Identität",
    technical: "Technik",
    created: "Fahrzeug angelegt",
    saved: "Fahrzeug gespeichert",
    deleted: "Fahrzeug gelöscht",
    deleteBody:
      "Seine Arbeitsaufträge und die Servicehistorie werden mit gelöscht. Der Lagerbestand ändert sich nicht.",
    openWork: "Offene Arbeiten",
    openWorkEmpty: "Keine offenen Arbeitsaufträge.",
    history: "Servicehistorie",
    historyEmpty: "Abgeschlossene Arbeitsaufträge erscheinen hier mit den verbauten Teilen.",
    shortages: "Benötigte Teile",
    shortagesBody: "Über alle geplanten und laufenden Arbeitsaufträge dieses Fahrzeugs.",
    orderMissing: "Fehlende Teile bestellen",
    totalSpent: "Teilekosten",
    lastService: "Letzter Service {{date}}",
    noService: "Noch kein Service erfasst",
    openWorkOrders_one: "{{count}} offener Arbeitsauftrag",
    openWorkOrders_other: "{{count}} offene Arbeitsaufträge",
    showArchived: "Archivierte anzeigen",
    archivedBanner: "Dieses Fahrzeug ist archiviert.",
    partsUsed_one: "{{count}} Teil",
    partsUsed_other: "{{count}} Teile",
  },
  workOrders: {
    title: "Arbeitsaufträge",
    new: "Neuer Arbeitsauftrag",
    edit: "Arbeitsauftrag bearbeiten",
    fromTemplate: "Aus Vorlage",
    tabOpen: "Offen",
    tabCompleted: "Abgeschlossen",
    tabAll: "Alle",
    emptyTitle: "Noch keine Arbeitsaufträge",
    emptyBody:
      "Ein Arbeitsauftrag ist eine Arbeit an einem Fahrzeug – ein Ölservice, eine VANOS-Überholung, ein Restaurierungsschritt. Füge die benötigten Teile hinzu und sieh sofort, was fehlt.",
    emptyCompleted: "Abgeschlossene Arbeitsaufträge erscheinen hier.",
    groupInProgress: "In Arbeit",
    groupPlanned: "Geplant",
    groupIdeas: "Ideen",
    groupCompleted: "Abgeschlossen",
    groupCancelled: "Abgebrochen",
    vehicle: "Fahrzeug",
    titlePlaceholder: "z. B. Ölservice",
    plannedOn: "Geplant für",
    created: "Arbeitsauftrag angelegt",
    saved: "Arbeitsauftrag gespeichert",
    deleted: "Arbeitsauftrag gelöscht",
    deleteBody:
      "Bereits verbaute Teile bleiben vom Bestand abgezogen. Um sie zurückzubuchen, öffne den Arbeitsauftrag zuerst erneut.",
    parts: "Teile",
    partsCount_one: "{{count}} Teil",
    partsCount_other: "{{count}} Teile",
    addPart: "Teil hinzufügen",
    addPartPlaceholder: "Teile oder Nummern suchen…",
    createPart: "Teil „{{name}}“ anlegen",
    partsEmpty:
      "Füge die Teile hinzu, die diese Arbeit braucht. Teile, die du noch nicht hast, sind kein Problem – sie werden als fehlend angezeigt.",
    tool: "Werkzeug",
    toolHint: "Wird benötigt, aber nicht verbraucht",
    linkedInStock: "Stattdessen auf Lager: {{name}} · {{qty}} frei",
    useInstead: "Stattdessen dieses nehmen",
    usingInstead: "Jetzt mit {{name}}",
    consumable: "Wird verbraucht",
    orderMissing: "Fehlende Teile bestellen",
    complete: "Abschließen",
    completeTitle: "Arbeitsauftrag abschließen",
    completeBody:
      "Die verbrauchten Mengen werden vom Bestand abgezogen und in der Servicehistorie des Fahrzeugs gespeichert.",
    completedOn: "Abgeschlossen am",
    mileageAtCompletion: "Kilometerstand",
    used: "Verbaut",
    completed: "Arbeitsauftrag abgeschlossen",
    completedBanner: "Abgeschlossen am {{date}}",
    completedBannerMileage: "Abgeschlossen am {{date}} bei {{mileage}}",
    reopen: "Erneut öffnen",
    reopenBody:
      "Verbaute Teile werden ins Lager zurückgebucht und der Arbeitsauftrag ist wieder in Arbeit.",
    reopened: "Arbeitsauftrag erneut geöffnet",
    saveAsTemplate: "Als Vorlage speichern",
    savedAsTemplate: "Vorlage gespeichert",
    estimatedCost: "Geschätzte Teilekosten",
    partsCost: "Teilekosten",
    unpriced_one: "{{count}} Teil ohne Preis",
    unpriced_other: "{{count}} Teile ohne Preis",
    priorityHint: "Der Bestand geht zuerst an laufende Arbeiten, dann nach geplantem Datum.",
    template: "Vorlage",
    orderedFor: "Für diesen Arbeitsauftrag bestellt",
    statusChanged: "Status: {{status}}",
    insufficientStock: "Nicht genug auf Lager – der Bestand wird negativ.",
    inDraft: "Im Entwurf",
    descriptionPlaceholder: "Arbeitsschritte, Drehmomente, Notizen…",
  },
  templates: {
    title: "Vorlagen",
    new: "Neue Vorlage",
    edit: "Vorlage bearbeiten",
    emptyTitle: "Noch keine Vorlagen",
    emptyBody:
      "Speichere wiederkehrende Arbeiten wie einen Ölservice einmal mit Teilen, Anleitungen und Dokumenten. Dann legst du Arbeitsaufträge daraus mit einem Klick an.",
    used_one: "{{count}}-mal verwendet",
    used_other: "{{count}}-mal verwendet",
    neverUsed: "Noch nicht verwendet",
    createWorkOrder: "Arbeitsauftrag anlegen",
    createWorkOrderTitle: "Arbeitsauftrag aus Vorlage anlegen",
    created: "Vorlage angelegt",
    saved: "Vorlage gespeichert",
    deleted: "Vorlage gelöscht",
    workOrders: "Arbeitsaufträge aus dieser Vorlage",
    partsEmpty: "Füge die Teile hinzu, die diese Arbeit üblicherweise braucht.",
    partsHint: "Die Mengen, die jeder Arbeitsauftrag aus dieser Vorlage braucht.",
    qtyColumn: "Benötigt je Auftrag",
    status: "Starten als",
    interval: "Serviceintervall",
    intervalBody:
      "Optional: wie oft diese Arbeit fällig ist – was zuerst eintritt. Es steht an der Vorlage; geplant wird damit nichts.",
    intervalTime: "Zeit",
    intervalTimeUnit: "Zeiteinheit",
    intervalDistance: "Laufleistung",
    months: "Monate",
    years: "Jahre",
    everyMonths_one: "jeden Monat",
    everyMonths_other: "alle {{count}} Monate",
    everyYears_one: "jedes Jahr",
    everyYears_other: "alle {{count}} Jahre",
    everyDistance: "alle {{distance}}",
    intervalEither: "{{period}} oder {{distance}}",
  },
  orders: {
    title: "Bestellungen",
    new: "Neue Bestellung",
    edit: "Bestellung bearbeiten",
    emptyTitle: "Noch keine Bestellungen",
    emptyBody:
      "Sammle Teile in einem Entwurf, markiere ihn als bestellt und bestätige Lieferungen – der Bestand aktualisiert sich von selbst.",
    name: "Name",
    namePlaceholder: "z. B. Teile für den Herbstservice",
    supplier: "Händler",
    noSupplier: "Kein Händler",
    orderNumber: "Bestellnummer",
    orderedOn: "Bestellt am",
    expectedOn: "Erwartet am",
    receivedOn: "Eingegangen am",
    trackingUrl: "Sendungsverfolgung",
    shipping: "Versand",
    currency: "Währung",
    subtotal: "Teile",
    lines: "Positionen",
    linesCount_one: "{{count}} Position",
    linesCount_other: "{{count}} Positionen",
    linesEmpty: "Füge dieser Bestellung Teile hinzu.",
    addLine: "Teil hinzufügen",
    ordered: "Bestellt",
    received: "Eingegangen",
    unitPrice: "Stückpreis",
    forWorkOrder: "Für",
    place: "Als bestellt markieren",
    placeTitle: "Als bestellt markieren",
    placeBody: "Die Teile dieser Bestellung zählen jetzt als bestellt.",
    placed: "Bestellung als bestellt markiert",
    receiveAll: "Alles eingegangen",
    receive: "Wareneingang…",
    receiveTitle: "Lieferung bestätigen",
    receiveBody: "Gib ein, was angekommen ist. Es wird sofort dem Bestand zugebucht.",
    receiveConfirm: "Einbuchen",
    receivedToast: "Lieferung eingebucht",
    undoReceipt: "Wareneingang rückgängig",
    receipts: "Wareneingänge",
    revertToDraft: "Zurück zum Entwurf",
    cancelOrder: "Bestellung stornieren",
    cancelled: "Bestellung storniert",
    reopenDraft: "Als Entwurf wieder öffnen",
    created: "Bestellung angelegt",
    saved: "Bestellung gespeichert",
    deleted: "Bestellung gelöscht",
    quickPurchase: "Im Laden gekauft",
    quickPurchaseTitle: "Im Laden gekauft",
    quickPurchaseBody:
      "Erfasse Teile, die du direkt gekauft hast. Sie werden sofort dem Bestand zugebucht.",
    quickPurchaseDefaultName: "Ladenkauf",
    quickPurchaseDone: "Einkauf eingebucht",
    purchasedOn: "Gekauft am",
    addToOrderTitle: "Zur Bestellung hinzufügen",
    addToOrderTarget: "Bestellung",
    addToOrderAuto: "Automatisch – ein Entwurf je bevorzugtem Händler",
    addToOrderNew: "Neue Bestellung bei {{name}}",
    addedToOrders_one: "Zum Entwurf „{{name}}“ hinzugefügt",
    addedToOrders_other: "Zu {{count}} Bestellentwürfen hinzugefügt",
    newDraftName: "Neue Bestellung",
    overdue: "Überfällig",
    arrivesIn: "Erwartet {{when}}",
    openShop: "Im Shop öffnen",
    deleteBody:
      "Die Bestellung und ihre Positionen werden entfernt. Der Bestand ändert sich nicht.",
  },
  toOrder: {
    title: "Zu bestellen",
    subtitle:
      "Alles, was für geplante Arbeiten und den Mindestbestand fehlt – gruppiert nach bevorzugtem Händler.",
    emptyTitle: "Nichts zu bestellen",
    emptyBody: "Alle geplanten Arbeiten sind durch Bestand und laufende Bestellungen abgedeckt.",
    noSupplier: "Kein bevorzugter Händler",
    belowMinimum: "Unter Mindestbestand",
    alreadyInDraft: "{{qty}} schon in einem Entwurf",
    addSelected: "Zu Bestellentwürfen hinzufügen",
    addGroup: "Zum Entwurf",
    selectAll: "Alle auswählen",
    covered: "Durch Entwürfe abgedeckt",
  },
  chassisCodes: {
    title: "Baureihen",
    add: "Baureihe hinzufügen",
    create: "Baureihe „{{name}}“ anlegen",
    any: "Alle Baureihen",
    fits: "Passt zu {{code}}",
    more: "+{{count}}",
    since: "seit {{year}}",
    lists: {
      bmw: "BMW",
      bmw_m: "BMW M",
      mini: "MINI",
      motorrad: "BMW Motorrad",
    },
  },
  settings: {
    members: "Mitglieder",
    title: "Einstellungen",
    general: "Allgemein",
    garageSettings: "Garagen-Einstellungen",
    garageSettingsBody: "Nur Garagen-Admins sehen und ändern diese Einstellungen.",
    garage: "Garage",
    garageBody: "Sprache, Währung, Einheiten und Preise für alle in dieser Garage.",
    language: "Sprache",
    languageHint: "Alle in dieser Garage sehen die App in dieser Sprache.",
    languageHintAccounts:
      "Die Sprache der Daten dieser Garage: Beispieldaten, BMW-Hauptgruppen und Einladungs-E-Mails. Jeder sieht die App in seiner eigenen Sprache, einstellbar im Profil.",
    theme: "Darstellung",
    themeSystem: "System",
    themeLight: "Hell",
    themeDark: "Dunkel",
    currency: "Standardwährung",
    mileageUnit: "Standardeinheit Kilometerstand",
    prices: "Preise",
    pricesIncl: "Inkl. MwSt.",
    pricesExcl: "Ohne MwSt.",
    pricesHintIncl:
      "Preise werden ohne MwSt. gespeichert und mit dem heute gültigen Satz angezeigt. Eine Bestellung behält den Satz ihres Bestelldatums.",
    pricesHintExcl: "Preise werden ohne MwSt. eingegeben und angezeigt.",
    taxRate: "MwSt.-Satz",
    taxRateHint:
      "Eine Änderung gilt ab ihrem Datum: Trag sie vorher ein, dann stellen sich die Preise an dem Tag um.",
    taxRateFrom: "Ab {{date}}",
    taxRateBefore: "Vor dem {{date}}",
    taxRateCurrent: "Aktuell",
    taxRateUpcoming: "Geplant",
    taxRateAdd: "Änderung hinzufügen",
    taxRateEdit: "MwSt.-Satz bearbeiten",
    taxRateChange: "Änderung des MwSt.-Satzes",
    taxRateValidFrom: "Gültig ab",
    taxRateUntil: "Bis",
    taxRateUntilHint: "Für eine befristete Änderung. Ab dem Tag danach gilt wieder der Satz davor.",
    taxRateRemove: "Änderung entfernen",
    locations: "Lagerorte",
    locationsBody: "Wo Teile liegen. Lagerorte lassen sich verschachteln, z. B. Garage › Regal A.",
    newLocation: "Neuer Lagerort",
    parentLocation: "Liegt in",
    topLevel: "Oberste Ebene",
    suppliers: "Händler",
    suppliersBody: "Shops, bei denen du bestellst.",
    newSupplier: "Neuer Händler",
    editSupplier: "Händler bearbeiten",
    supplierKind: "Art",
    supplierOnline: "Onlineshop",
    supplierLocal: "Händler vor Ort",
    supplierEmail: "E-Mail Teileservice",
    supplierEmailHint: "Hierhin kannst du Teilelisten mailen.",
    website: "Website",
    tags: "Schlagwörter",
    tagsBody: "Teile gruppieren, z. B. Motor, Bremsen, Verbrauchsmaterial.",
    newTag: "Neues Schlagwort",
    mainGroups: "BMW-Hauptgruppen",
    mainGroupsBody:
      "Füge die Hauptgruppen aus dem BMW-Teilekatalog als Schlagwörter hinzu, nummeriert wie BMW-Teilenummern (z. B. 34 Bremsen). Sie werden in der aktuellen Sprache angelegt und funktionieren dann wie jedes andere Schlagwort.",
    mainGroupsMissing: "Noch nicht hinzugefügt",
    addMainGroups: "Hauptgruppen hinzufügen",
    mainGroupsAdded_one: "{{count}} Schlagwort hinzugefügt",
    mainGroupsAdded_other: "{{count}} Schlagwörter hinzugefügt",
    mainGroupsPresent: "Alle Hauptgruppen sind schon vorhanden",
    trademarkShort:
      "BMW und MINI sind eingetragene Marken der BMW AG. Bimmertools Inventory steht in keiner Verbindung zu BMW.",
    chassisCodes: "Baureihen",
    chassisCodesBody:
      "Markiere, zu welchen Fahrzeugen ein Teil oder eine Vorlage passt, z. B. E90 oder F80. Arbeitsaufträge zeigen dann die Teile, die zu ihrem Fahrzeug passen, zuerst.",
    newChassisCode: "Neue Baureihe",
    chassisCodesPrefill: "Aus einer Liste übernehmen",
    chassisCodesPrefillBody:
      "Wähle die Baureihen von BMW-, BMW M-, MINI- oder BMW-Motorrad-Modellen aus, statt sie einzutippen.",
    chooseChassisCodes: "Baureihen auswählen",
    chassisCodeList: "Liste",
    chassisCodesVariants:
      "Varianten haben eine eigene Baureihe, z. B. E92 (M3), E36/2S (M3), G26 (i4): Ein Teil für E92 passt nicht automatisch zu E92 (M3). Ein Teil für E46 passt zu jeder E46-Karosserie (E46/2 Coupé, E46/3 Touring …).",
    selectAll: "Alle auswählen",
    alreadyAdded: "Vorhanden",
    noChassisCodeMatch: "Keine Baureihe gefunden.",
    addChassisCodes_zero: "Baureihen hinzufügen",
    addChassisCodes_one: "{{count}} Baureihe hinzufügen",
    addChassisCodes_other: "{{count}} Baureihen hinzufügen",
    chassisCodesAdded_one: "{{count}} Baureihe hinzugefügt",
    chassisCodesAdded_other: "{{count}} Baureihen hinzugefügt",
    templateCount_one: "{{count}} Vorlage",
    templateCount_other: "{{count}} Vorlagen",
    vehicleCount_one: "{{count}} Fahrzeug",
    vehicleCount_other: "{{count}} Fahrzeuge",
    color: "Farbe",
    chassisCodeDefaultColor: "Standardfarbe dieses Codes",
    data: "Daten",
    installation: "Installation",
    installationBody: "Alles hier gilt für alle Garagen dieser Installation.",
    backups: "Sicherungen",
    backupsBody:
      "Einmal am Tag wird automatisch eine Kopie der ganzen App (aller Garagen) gespeichert.",
    autoBackup: "Tägliche automatische Sicherung",
    backupDir: "Sicherungsordner",
    backupDirDefault: "App-Datenordner",
    chooseFolder: "Ordner wählen…",
    backupKeep: "Behalte die letzten",
    backupNow: "Jetzt sichern",
    backupDone: "Sicherung gespeichert",
    lastBackup: "Letzte Sicherung {{when}}",
    neverBackedUp: "Noch keine Sicherung",
    backupsCount_one: "{{count}} Sicherung",
    backupsCount_other: "{{count}} Sicherungen",
    recentBackups: "Letzte Sicherungen",
    useDefault: "Standard verwenden",
    export: "Alles exportieren",
    exportBody: "Eine einzige Datei mit allen Garagen, ihren Bildern und Dokumenten.",
    import: "Aus Datei wiederherstellen…",
    importBody: "Ersetzt alles in dieser App (alle Garagen) durch den Inhalt eines Exports.",
    importConfirm: "Alle Daten ersetzen?",
    importConfirmBody:
      "Alles, was gerade in der App ist, wird durch den Inhalt der Datei ersetzt. Exportiere vorher, wenn du unsicher bist.",
    importDone: "Daten wiederhergestellt",
    demo: "Beispieldaten",
    demoBody:
      "Lade Beispielfahrzeuge, Teile, Arbeitsaufträge und Bestellungen, um die App auszuprobieren.",
    loadDemo: "Beispieldaten laden",
    demoLoaded: "Beispieldaten geladen",
    about: "Über",
    version: "Version {{version}}",
    trademarks:
      "BMW, BMW M, M Performance, BMW Motorrad und MINI sind eingetragene Marken der BMW Group, der BMW AG oder der BMW M GmbH. Alle Rechte vorbehalten. Alle genannten Marken gehören ihren jeweiligen Herstellern und Vertreibern. Diese App steht in keiner Verbindung zu diesen Unternehmen. Die Markennamen werden ausschließlich genannt, damit Nutzer die Produkte besser zuordnen können. Die Hersteller stehen weder in Verbindung mit den Inhalten dieser App noch haften sie dafür.",
    licence: "Proprietäre Software, alle Rechte vorbehalten. Sie enthält Open-Source-Software:",
    openSourceLicences: "Open-Source-Lizenzen",
    dataFolder: "Datenordner",
    syncWarning:
      "Dein Datenordner liegt in einem Cloud-synchronisierten Ordner. Das Synchronisieren einer laufenden Datenbank kann sie beschädigen – leg stattdessen die Sicherungen dort ab.",
    saved: "Einstellungen gespeichert",
    partCount_one: "{{count}} Teil",
    partCount_other: "{{count}} Teile",
    orderCount_one: "{{count}} Bestellung",
    orderCount_other: "{{count}} Bestellungen",
  },
  currentVehicle: {
    title: "Aktuelles Fahrzeug",
    all: "Alle Fahrzeuge",
    label: "Aktuelles Fahrzeug: {{vehicle}}",
    labelAll: "Aktuelles Fahrzeug: alle Fahrzeuge",
    notice:
      "Es wird nur gezeigt, was zu {{vehicle}} gehört: seine Arbeitsaufträge sowie Teile und Vorlagen, die zu {{code}} passen oder keine Baureihe haben.",
    noticeNoCode:
      "Es werden nur die Arbeitsaufträge und Bestellungen von {{vehicle}} gezeigt. Es hat keine Baureihe, darum erscheinen alle Teile und Vorlagen.",
    setChassisCode: "Baureihe eintragen",
    showAll: "Alle zeigen",
    workOn: "An diesem Fahrzeug arbeiten",
    current: "Aktuelles Fahrzeug",
  },
  partsList: {
    title: "Teileliste",
    print: "Liste drucken",
    body: "Wähle die Teile und was auf der Liste steht. Dann druck sie für die Theke aus oder schick sie dem Händler per E-Mail.",
    for: "Für",
    noDealer: "Kein Händler",
    show: "Anzeigen",
    genuineOnly: "Nur Originalteile",
    genuineOnlyHint:
      "Für einen Händler, der Originalteile verkauft: OE- und Nachbauteile werden zu ihrem verknüpften Originalteil.",
    swapped_one: "{{count}} Teil wird zu seinem Originalteil.",
    swapped_other: "{{count}} Teile werden zu ihrem Originalteil.",
    leftOut: "Weggelassen, kein Originalteil verknüpft: {{names}}",
    leftOutLine: "Weggelassen: kein Originalteil verknüpft",
    genuineNumber: "Original {{number}}",
    insteadOf: "statt {{name}}",
    onlinePrices: "Onlinepreise zum Vergleich",
    onlinePricesHint:
      "Der günstigste Preis deiner Onlineshops und eine Spalte für den Preis beim Händler.",
    vehicleDetails: "Fahrzeugdaten (FIN)",
    parts: "Teile",
    selected: "{{count}} von {{total}} ausgewählt",
    onlyMissing: "Nur fehlende",
    note: "Notiz",
    noteHint: "Steht unten auf der Liste, z. B. dein Name und deine Telefonnummer.",
    copy: "Als Text kopieren",
    copied: "Teileliste kopiert",
    email: "E-Mail",
    emailTo:
      "Öffnet eine neue E-Mail an {{email}} in deinem Mailprogramm. Senden musst du sie selbst.",
    emailNoAddress:
      "Öffnet eine neue E-Mail in deinem Mailprogramm. Mit der E-Mail-Adresse des Teileservice in den Einstellungen steht der Empfänger gleich drin.",
    emailTooLong:
      "Die Liste ist zu lang für einen E-Mail-Link und wurde stattdessen kopiert. Füge sie in die E-Mail ein.",
    printAction: "Drucken",
    subject: "Teileliste – {{what}}",
    number: "Teilenummer",
    part: "Teil",
    amount: "Menge",
    online: "Online",
    dealer: "Händler",
    onlineTotal: "Summe online",
    onlinePrice: "online {{price}}",
  },
  prices: {
    inclTax: "inkl. MwSt.",
    inclTaxRate: "inkl. {{rate}} MwSt.",
    exclTax: "ohne MwSt.",
    column: "Preis",
    enteredOn: "Preis vom {{date}}",
    previous: "Vorher {{price}} am {{date}}",
    estimate: "Geschätzte Kosten",
    unpriced_one: "{{count}} Teil ohne Preis",
    unpriced_other: "{{count}} Teile ohne Preis",
  },
  garages: {
    garage: "Garage",
    title: "Garagen",
    current: "Garage: {{name}}",
    new: "Neue Garage…",
    newTitle: "Neue Garage",
    newBody:
      "Eine Garage hat ihre eigenen Teile, Fahrzeuge, Arbeitsaufträge, Vorlagen, Bestellungen und Einstellungen.",
    rename: "Garage umbenennen…",
    renameTitle: "Garage umbenennen",
    renameWarning:
      "Die Adresse ändert sich mit: Links und Lesezeichen auf die alte funktionieren nicht mehr.",
    nameHint: "Der Name ist auch die Adresse: Kleinbuchstaben, Ziffern und Bindestriche.",
    address: "Adresse",
    create: "Garage anlegen",
    tooShort: "Mindestens 3 Zeichen.",
    tooLong: "Höchstens 50 Zeichen.",
    reserved: "Namen dürfen nicht mit „{{word}}“ beginnen.",
    taken: "Es gibt schon eine Garage mit diesem Namen.",
    notFound: "Es gibt keine Garage „{{name}}“",
    notFoundBody: "Öffne stattdessen eine von diesen:",
    none: "Es gibt noch keine Garage.",
    notYours: "„{{name}}“ ist keine deiner Garagen",
    notYoursBody: "Es gibt sie nicht, oder du bist kein Mitglied. Öffne eine von deinen:",
    noneMember:
      "Du bist noch in keiner Garage Mitglied. Bitte einen Garagen-Admin, dich einzuladen.",
  },
  roles: {
    admin: "Administrator",
    garage_admin: "Garagen-Admin",
    garage_user: "Garagen-Benutzer",
    garage_visitor: "Besucher",
  },
  roleInText: {
    admin: "Administrator",
    garage_admin: "Garagen-Admin",
    garage_user: "Garagen-Benutzer",
    garage_visitor: "Besucher",
  },
  roleHelp: {
    garage_admin: "Nutzt die Garage und verwaltet ihre Mitglieder und Garagen-Einstellungen.",
    garage_user: "Nutzt die Garage.",
    garage_visitor: "Probiert die App in einer eigenen Demo-Garage aus.",
  },
  userStatus: {
    active: "Aktiv",
    disabled: "Gesperrt",
    deleted: "Gelöschtes Konto",
  },
  memberStatus: {
    active: "Aktiv",
    deactivated: "Deaktiviert",
    invited: "Eingeladen bis {{date}}",
    expired: "Einladung abgelaufen",
  },
  account: {
    email: "E-Mail-Adresse",
    password: "Passwort",
    firstName: "Vorname",
    lastName: "Nachname",
    newPassword: "Neues Passwort",
    repeatPassword: "Passwort wiederholen",
    passwordHint: "Mindestens 8 Zeichen. Ein paar Wörter ergeben ein gutes.",
    backToSignIn: "Zurück zur Anmeldung",
    backToGarages: "Zurück zu den Garagen",
    signInTitle: "Anmelden",
    signInBody: "Willkommen zurück bei Bimmertools Inventory.",
    signIn: "Anmelden",
    forgot: "Passwort vergessen?",
    forgotTitle: "Passwort vergessen?",
    forgotBody: "Gib deine E-Mail-Adresse ein, dann bekommst du einen Link für ein neues Passwort.",
    sendLink: "Link senden",
    sentTitle: "Schau in dein Postfach",
    sentBody:
      "Wenn es ein Konto für {{email}} gibt, ist ein Link für ein neues Passwort unterwegs. Er gilt eine Stunde.",
    resetTitle: "Neues Passwort wählen",
    resetBody: "Damit wirst du auf allen Geräten abgemeldet.",
    savePassword: "Passwort speichern",
    resetDone: "Passwort geändert. Melde dich mit dem neuen Passwort an.",
    inviteGarageTitle: "Willkommen in „{{garage}}“",
    inviteAdminTitle: "Bimmertools Inventory betreuen",
    invitedBy: "{{inviter}} hat dich als {{role}} eingeladen.",
    invitedAs: "Du bist als {{role}} eingeladen.",
    validUntil: "Die Einladung gilt bis {{date}}.",
    existingAccount:
      "Für {{email}} gibt es schon ein Konto. Melde dich an, um die Einladung anzunehmen.",
    signedInAs: "Du bist als {{email}} angemeldet.",
    accept: "Einladung annehmen",
    signInAndAccept: "Anmelden und annehmen",
    invalidTitle: "Diese Einladung gilt nicht mehr",
    invalidBody:
      "Sie ist abgelaufen, wurde schon angenommen oder zurückgezogen. Bitte die Person, die dich eingeladen hat, um eine neue.",
    nameAndPicture: "Name und Bild",
    preferences: "Sprache und Darstellung",
    preferencesBody: "Gilt auf jedem Gerät, auf dem du dich anmeldest.",
    profileTitle: "Profil",
    profileBody: "Name und Bild sehen die anderen Mitglieder deiner Garagen.",
    emailHint: "Mit dieser Adresse meldest du dich an. Sie lässt sich noch nicht ändern.",
    createdOn: "Konto erstellt",
    lastSignIn: "Letzte Anmeldung",
    uploadPicture: "Bild hochladen",
    removePicture: "Bild entfernen",
    profileSaved: "Profil gespeichert",
    pictureSaved: "Bild gespeichert",
    changePassword: "Passwort ändern",
    changePasswordBody: "Andere Geräte werden dabei abgemeldet.",
    currentPassword: "Aktuelles Passwort",
    passwordChanged: "Passwort geändert",
    deleteTitle: "Mein Konto löschen",
    deleteBody:
      "Löscht dein Konto und deine persönlichen Daten sofort: Name, E-Mail-Adresse, Bild, Passwort, Mitgliedschaften und Einladungen an dich. Was du in Garagen eingetragen hast (Teile, Bestellungen …), gehört der Garage und bleibt. Das lässt sich nicht rückgängig machen.",
    deleteConfirmTitle: "Konto endgültig löschen?",
    deletePassword: "Zur Bestätigung dein Passwort",
    deleteConfirm: "Mein Konto löschen",
    deleted: "Dein Konto wurde gelöscht.",
    disabledTitle: "Dein Konto wurde gesperrt",
    disabledBody:
      "Ein Administrator hat es gesperrt, deshalb kannst du gerade keine Garage öffnen. Frag nach, falls das ein Versehen ist. Löschen kannst du dein Konto trotzdem.",
    deletedTitle: "Dein Konto wurde gelöscht",
    deletedBody:
      "Ein Administrator hat dich aus Bimmertools Inventory entfernt. Deine Anmeldung gibt es noch: Lösche sie, um alles zu entfernen, oder nimm eine neue Einladung an, um zurückzukommen.",
    menu: "Konto",
    profile: "Profil",
    admin: "Administration",
    signOut: "Abmelden",
  },
  welcome: {
    step: "Willkommen · Schritt {{step}} von {{steps}}",
    languageTitle: "Welche Sprache möchtest du?",
    languageBody:
      "Die App verwendet sie auf jedem Gerät, auf dem du dich anmeldest. Ändern kannst du sie in deinem Profil.",
    themeTitle: "Wie soll die App aussehen?",
    themeBody: "Hell, dunkel oder wie dein Gerät. Auch das kannst du in deinem Profil ändern.",
    garageTitle: "Deine Demo-Garage",
    garageBody: "Sie gehört dir allein und wird am {{date}} mit allem darin gelöscht.",
    exampleData: "Mit Beispieldaten",
    exampleDataBody:
      "Teile, Fahrzeuge, Arbeitsaufträge und Bestellungen, um gleich alles auszuprobieren.",
    empty: "Leer",
    emptyBody:
      "Fang mit deinen eigenen Teilen und Fahrzeugen an. „Demo-Daten zurücksetzen“ füllt sie später.",
    continue: "Weiter",
    back: "Zurück",
    finish: "Los geht's",
    create: "Demo-Garage anlegen",
  },
  demo: {
    requestTitle: "Bimmertools Inventory ausprobieren",
    requestBody:
      "Hol dir eine eigene Demo-Garage, mit Beispielteilen, Fahrzeugen und Arbeitsaufträgen oder leer, und probiere alles aus. Sie wird {{duration}} nach dem Öffnen des Links mit allem darin gelöscht.",
    hours_one: "{{count}} Stunde",
    hours_other: "{{count}} Stunden",
    minutes_one: "{{count}} Minute",
    minutes_other: "{{count}} Minuten",
    email: "Deine E-Mail-Adresse",
    send: "Link zusenden",
    stored:
      "Deine E-Mail-Adresse dient nur dazu, dir den Link zu senden, und wird mit deiner Demo-Garage gelöscht.",
    privacy: "Datenschutz",
    honeypot: "Dieses Feld leer lassen",
    sentTitle: "Schau in dein Postfach",
    sentBody:
      "Ein Link zu deiner Demo-Garage ist an {{email}} unterwegs. Er funktioniert einmal und eine Stunde lang. Hast du schon eine Demo-Garage, führt er dorthin zurück.",
    otherEmail: "Andere E-Mail-Adresse verwenden",
    ended:
      "Deine Demo-Garage wurde gelöscht. Danke, dass du Bimmertools Inventory ausprobiert hast!",
    visitor: "Demo-Besucher",
    banner: "Demo",
    deletedIn: "wird in {{hours}} Std. gelöscht",
    reset: "Demo-Daten zurücksetzen",
    resetTitle: "Mit den Beispieldaten neu anfangen?",
    resetBody:
      "Alles in dieser Demo-Garage wird durch die Beispieldaten ersetzt, deine Änderungen gehen verloren.",
    resetDone: "Die Beispieldaten sind wieder da.",
    end: "Demo jetzt beenden",
    endTitle: "Demo jetzt beenden?",
    endBody:
      "Deine Demo-Garage wird mit allem darin gelöscht, und du wirst abgemeldet. Das lässt sich nicht rückgängig machen.",
    profileBody: "Du probierst Bimmertools Inventory in einer eigenen Demo-Garage aus.",
    deletedOn: "Deine Demo-Garage wird am {{date}} gelöscht.",
  },
  members: {
    title: "Mitglieder",
    body: "Wer diese Garage öffnen kann und wer sie verwaltet.",
    invite: "Einladen…",
    inviteTitle: "Jemanden einladen",
    inviteBody: "Die Person bekommt eine E-Mail mit einem Link, um der Garage beizutreten.",
    role: "Rolle",
    send: "Einladung senden",
    invited: "Einladung an {{email}} gesendet",
    invitations: "Einladungen",
    invitedBy: "Eingeladen von {{name}}",
    resend: "Erneut senden",
    resent: "Einladung erneut gesendet",
    revoke: "Zurückziehen",
    revoked: "Einladung zurückgezogen",
    you: "du",
    lastSignIn: "Letzte Anmeldung {{when}}",
    neverSignedIn: "Noch nie angemeldet",
    accountDisabled: "Konto gesperrt",
    actions: "Aktionen für {{name}}",
    makeGarageAdmin: "Zum Garagen-Admin machen",
    makeGarageUser: "Zum Garagen-Benutzer machen",
    deactivate: "Deaktivieren",
    reactivate: "Wieder aktivieren",
    remove: "Aus der Garage entfernen…",
    removeTitle: "{{name}} aus „{{garage}}“ entfernen?",
    removeBody: "Die Person kann diese Garage nicht mehr öffnen. Was sie eingetragen hat, bleibt.",
    empty: "Noch keine Mitglieder.",
  },
  admin: {
    title: "Administration",
    garages: "Garagen",
    users: "Benutzer",
    settings: "Einstellungen",
    backToGarages: "Zurück zu den Garagen",
    notAllowedTitle: "Das ist für Administratoren",
    notAllowedBody: "Mit deinem Konto kannst du die Administration nicht öffnen.",
    garagesBody: "Alle Garagen dieses Dienstes mit ihren Mitgliedern.",
    newGarage: "Neue Garage…",
    newGarageTitle: "Neue Garage",
    newGarageBody:
      "Gib ihr einen Namen und lade, wenn du magst, die ersten Benutzer ein. Ohne eingeladenen Garagen-Admin wirst du selbst Garagen-Admin.",
    firstUsers: "Personen einladen (optional)",
    addPerson: "Person hinzufügen",
    removePerson: "Person entfernen",
    createAndInvite: "Anlegen und Einladungen senden",
    create: "Garage anlegen",
    garageCreated: "Garage angelegt, Einladungen gesendet",
    garageCreatedAlone: "Garage angelegt",
    unsent:
      "Diese Einladungen konnten nicht gesendet werden: {{emails}}. Sende sie auf der Seite der Garage erneut.",
    membersCount_one: "{{count}} Mitglied",
    membersCount_other: "{{count}} Mitglieder",
    garageAdminsCount_one: "{{count}} Garagen-Admin",
    garageAdminsCount_other: "{{count}} Garagen-Admins",
    invitedCount_one: "{{count}} eingeladen",
    invitedCount_other: "{{count}} eingeladen",
    noGarageAdmin: "Kein Garagen-Admin",
    createdOn: "Angelegt am {{date}}",
    openGarage: "Garage öffnen",
    renameGarage: "Umbenennen…",
    noGarages: "Noch keine Garagen.",
    usersBody: "Alle mit einem Konto und die Garagen, in denen sie sind.",
    search: "Name oder E-Mail suchen",
    noUsers: "Keine Benutzer gefunden.",
    noGarage: "Keine Garage",
    you: "Du",
    lastSignIn: "Letzte Anmeldung",
    created: "Erstellt",
    never: "Nie",
    makeAdmin: "Zum Administrator machen",
    removeAdmin: "Administratorrolle entziehen",
    disable: "Sperren",
    enable: "Entsperren",
    delete: "Löschen…",
    deleteTitle: "{{name}} löschen?",
    deleteBody:
      "Die Person wird aus allen Garagen entfernt und ihr Profil geleert. Sie kann sich weiter anmelden, um das zu sehen, und ihre Anmeldung selbst löschen. Mit einer neuen Einladung ist sie wieder Benutzer.",
    userActions: "Aktionen für {{name}}",
    settingsBody: "Einstellungen für den ganzen Dienst.",
    inviteDays: "Einladungen gelten",
    days: "Tage",
    inviteDaysHint: "1 bis 90 Tage. Gilt für Einladungen, die ab jetzt gesendet werden.",
    saved: "Gespeichert",
  },
  demoPackage: {
    title: "Demo-Paket",
    body: "Beispieldaten zum Ausprobieren. Ein Import ersetzt die Daten dieser Garage; andere Garagen bleiben, wie sie sind.",
    download: "Demo-Paket herunterladen",
    downloadBody: "Eine Datei pro Sprache.",
    import: "Demo-Paket importieren…",
    importBody: "Lade ein heruntergeladenes Demo-Paket in diese Garage.",
    importConfirm: "Daten ersetzen",
    confirm: "Alles in „{{garage}}“ ersetzen?",
    confirmBody:
      "Ihre Teile, Fahrzeuge, Arbeitsaufträge, Vorlagen, Bestellungen, Schlagwörter, Baureihen, Lagerorte, Händler, Bilder, Dokumente und Einstellungen werden durch das Demo-Paket ersetzt.",
    backupFirst: "Vorher wird eine Sicherung der App gespeichert.",
    madeWith: "Erstellt mit Version {{version}} am {{date}}",
    workOrderCount_one: "{{count}} Arbeitsauftrag",
    workOrderCount_other: "{{count}} Arbeitsaufträge",
    tooNew:
      "Erstellt mit Version {{version}}: Aktualisiere Bimmertools Inventory, um es zu importieren.",
    imported: "Demo-Paket importiert",
    export: "Demo-Paket exportieren",
    exportBody: "Diese Garage als Demo-Paket, gekennzeichnet als {{language}}.",
  },
  palette: {
    placeholder: "Teile, Nummern, Fahrzeuge, Arbeitsaufträge suchen…",
    actions: "Aktionen",
    navigation: "Gehe zu",
    parts: "Teile",
    vehicles: "Fahrzeuge",
    workOrders: "Arbeitsaufträge",
    orders: "Bestellungen",
    empty: "Nichts gefunden.",
    onHand: "{{qty}} auf Lager",
  },
  attachments: {
    addImage: "Bild hinzufügen",
    dropHere: "Dateien hierher ziehen oder klicken zum Hochladen",
    dropImages: "Bilder hierher ziehen, einfügen oder klicken zum Hochladen",
    uploading: "Wird hochgeladen…",
    uploaded: "Hochgeladen",
    makeCover: "Als Titelbild verwenden",
    download: "Herunterladen",
  },
  links: {
    add: "Link hinzufügen",
    url: "Webadresse",
    title: "Titel",
    titleHint: "Leer lassen, um den Seitentitel zu übernehmen.",
  },
  markdown: {
    write: "Schreiben",
    preview: "Vorschau",
    hint: "Markdown wird unterstützt",
    empty: "Nichts zum Anzeigen.",
  },
};
